import { useMemo, useState } from "react";
import PayrollCompanyLayout from "../../../components/layouts/PayrollCompanyLayout";
import { Helmet } from "react-helmet-async";
import CustomTab from "../../../components/custom_tab/CustomTab";
import { PayrollTabColumns } from "../../../components/custom_tab/tabColumns";
import { useSearchParams } from "react-router-dom";
import useUserStore from "../../../state-management/useUserStore";
import HumanResourceLayout from "../../../components/layouts/HumanResourceLayout";
import CompanyPayrollRuns from "./views/CompanyPayrollRuns";
import CompanyPayslips from "./views/CompanyPayslips";
import useCompanyStore from "../../../state-management/useCompanyStore";
import { useAsyncEffect } from "ahooks";
import API from '../../../services/utils/api';
import CompanyPayrollReports from "./views/CompanyPayrollReports";
import { 
	PermissionChecker, 
	PERMISSIONS, 
	useCheckUserPermissions, 
	useValidateUserPermissions 
} from "../../../helpers/usePermissionManager";

const Payroll = () => {
	const hasPermission = useValidateUserPermissions(
		[PERMISSIONS.PAYSLIPS_READ, PERMISSIONS.PAYSLIPS_WRITE, PERMISSIONS.PAYROLL_WRITE],
		"OR"
	);
	const { checkUserPermissionsOr } = useCheckUserPermissions();
	const { userType } = useUserStore();
	const LAYOUT = useMemo(() => {
		switch(userType) {
			case "ORGANIZATION": 
				return PayrollCompanyLayout;
			default: 
				return HumanResourceLayout;
		}
	}, [userType])
	const { currentCompany } = useCompanyStore();
	const [searchParams, setSearchParams] = useSearchParams();
	const [view, setView] = useState(
		searchParams.get("view") || (
			!checkUserPermissionsOr([PERMISSIONS.PAYROLL_WRITE, PERMISSIONS.PAYSLIPS_WRITE])
			? "companyPayslips"
			: "companyPayrollRuns"
		)
	);
	const [recentPayrollRun, setRecentPayrollRun] = useState<any>();

	const changeView = (item: any) => {
		setView(item.view);
		setSearchParams({ view: item.view });
	};

	useAsyncEffect(async () => {
		// Drop the previous company's pay period before loading this one's
		setRecentPayrollRun(undefined);
		if (!currentCompany?.id) return;
		try {
			const response = await API.PayrollAPI.getRecentPayrollRun(
				currentCompany.id, {}
			);
			setRecentPayrollRun(response.data);
		} catch (error) {
			API.Handlers.onErrorHandler(error, null, null);
		}
	}, [currentCompany])
	
	return (hasPermission &&
		<LAYOUT>
			<Helmet>
				{userType === "HR" ? (
					<title>Payroll Management | HR Flow</title>
				):(
					<title>Payroll | Company Flow</title>
				)}
			</Helmet>
			<h1 className="font-bold text-xl mb-8">Payroll {userType === "HR" ? " Management" : ""}</h1>
			<div className="flex flex-col">
				<div className="flex space-x-6 border-b overflow-auto children-scroll-bar whitespace-nowrap w-full mobile-full-width max-lg:pl-4">
					{PayrollTabColumns.map((item, index) => (
						<PermissionChecker 
							key={index}
							permissionRule="OR" 
							permissions={
								item.view === "companyPayrollRuns" 
									? [PERMISSIONS.PAYROLL_WRITE, PERMISSIONS.PAYSLIPS_WRITE] 
									: [PERMISSIONS.PAYSLIPS_READ]
							}
						>
							<button
								onClick={() => changeView(item)}
								className={`flex pb-3 max-lg:text-sm ${
									view === item.view
										? "border-b-2 border-primary-mango text-charcoal"
										: "text-gray-400"
								}`}
							>
								{item.name}
							</button>
						</PermissionChecker>
					))}
				</div>
			</div>
			{view === "companyPayrollRuns" && <CompanyPayrollRuns />}
			{view === "companyPayslips" && <CompanyPayslips defaultPayPeriod={recentPayrollRun} />}
			{view === "companyPayrollReports" && <CompanyPayrollReports defaultPayPeriod={recentPayrollRun} />}
		</LAYOUT>
	);
};

export default Payroll;
